import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { AIRPORTS, CITIES } from "@/i18n";
import { Plane, Hotel, Landmark, Palmtree, Search, ArrowRightLeft, Plus, X, Users } from "lucide-react";
import { HotelGuestPicker } from "@/components/HotelGuestPicker";
import { PassengerPicker } from "@/components/PassengerPicker";

const TABS = [
  { id: "flights", icon: Plane },
  { id: "hotels", icon: Hotel },
  { id: "religious", icon: Landmark },
  { id: "tours", icon: Palmtree },
];

export const HeroSearch = () => {
  const { t, lang } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("flights");
  const [trip, setTrip] = useState("oneway");
  const [fl, setFl] = useState({ origin: "BGW", destination: "IST", date: "", return: "" });
  const [pax, setPax] = useState({ adults: 1, children: 0, infants: 0 });
  const [legs, setLegs] = useState([{ origin: "BGW", destination: "IST", date: "" }, { origin: "IST", destination: "DXB", date: "" }]);
  const [ht, setHt] = useState({ city: "baghdad", check_in: "", check_out: "", rooms: 1, adults: 2, children_ages: [] });
  const [showGuests, setShowGuests] = useState(false);

  const setLeg = (i, patch) => setLegs(legs.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const submit = () => {
    if (tab === "flights") {
      if (trip === "multi") {
        const q = new URLSearchParams({ trip, ...pax, legs: legs.map((l) => `${l.origin}_${l.destination}_${l.date}`).join(",") });
        nav(`/flights?${q}`);
      } else {
        const q = new URLSearchParams({ trip, origin: fl.origin, destination: fl.destination, date: fl.date, ...pax, ...(trip === "round" ? { return: fl.return } : {}) });
        nav(`/flights?${q}`);
      }
    } else if (tab === "hotels") {
      const q = new URLSearchParams({ city: ht.city, check_in: ht.check_in, check_out: ht.check_out, rooms: ht.rooms, adults: ht.adults, children: ht.children_ages.join(",") });
      nav(`/hotels?${q}`);
    } else if (tab === "religious") {
      nav("/packages?cat=religious");
    } else {
      nav("/packages");
    }
  };

  const sel = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-tigris focus:ring-2 focus:ring-tigris/20 outline-none text-sm font-medium";
  const label = "block text-xs font-semibold text-slate-500 mb-1.5";

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-3xl p-5 sm:p-7 shadow-2xl border border-white/60">
      <div className="flex gap-1.5 sm:gap-2 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((tb) => {
          const Icon = tb.icon;
          const active = tab === tb.id;
          return (
            <button key={tb.id} data-testid={`hero-tab-${tb.id}`} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition ${active ? "bg-tigris text-white shadow-lg shadow-tigris/30" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon size={17} /> {t.tabs[tb.id]}
            </button>
          );
        })}
      </div>

      {tab === "flights" && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap" data-testid="trip-type-group">
            {["oneway", "round", "multi"].map((tp) => (
              <button key={tp} type="button" data-testid={`trip-type-${tp}`} onClick={() => setTrip(tp)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${trip === tp ? "bg-gold text-white border-gold" : "bg-white text-slate-600 border-slate-200 hover:border-gold"}`}>
                {t.flights.types[tp]}
              </button>
            ))}
          </div>
          {trip !== "multi" ? (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${trip === "round" ? "lg:grid-cols-6" : "lg:grid-cols-5"} gap-3 items-end`}>
              <div>
                <label className={label}>{t.search.from}</label>
                <select data-testid="flight-origin" className={sel} value={fl.origin} onChange={(e) => setFl({ ...fl, origin: e.target.value })}>
                  {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a[lang]} ({a.code})</option>)}
                </select>
              </div>
              <div>
                <label className={label}>{t.search.to}</label>
                <select data-testid="flight-destination" className={sel} value={fl.destination} onChange={(e) => setFl({ ...fl, destination: e.target.value })}>
                  {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a[lang]} ({a.code})</option>)}
                </select>
              </div>
              <div>
                <label className={label}>{trip === "round" ? t.flights.departDate : t.search.date}</label>
                <input data-testid="flight-date" type="date" className={sel} value={fl.date} onChange={(e) => setFl({ ...fl, date: e.target.value })} />
              </div>
              {trip === "round" && (
                <div>
                  <label className={label}>{t.flights.returnDate}</label>
                  <input data-testid="flight-return-date" type="date" className={sel} min={fl.date || undefined} value={fl.return} onChange={(e) => setFl({ ...fl, return: e.target.value })} />
                </div>
              )}
              <div>
                <label className={label}>{t.search.passengers}</label>
                <PassengerPicker value={pax} onChange={setPax} className={sel} />
              </div>
              <button data-testid="flight-search-submit" onClick={submit} className="btn-primary py-3 rounded-xl font-bold flex items-center justify-center gap-2 h-[46px]">
                <Search size={18} /> {t.hero.searchBtn}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {legs.map((l, i) => (
                <div key={i} data-testid={`multi-leg-${i}`} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[auto_1fr_1fr_1fr_auto] gap-3 items-end">
                  <span className="hidden lg:flex w-9 h-[46px] items-center justify-center rounded-xl bg-tigris/10 text-tigris text-sm font-bold">{i + 1}</span>
                  <div>
                    <label className={label}>{t.search.from}</label>
                    <select data-testid={`leg-origin-${i}`} className={sel} value={l.origin} onChange={(e) => setLeg(i, { origin: e.target.value })}>
                      {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a[lang]} ({a.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>{t.search.to}</label>
                    <select data-testid={`leg-destination-${i}`} className={sel} value={l.destination} onChange={(e) => setLeg(i, { destination: e.target.value })}>
                      {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a[lang]} ({a.code})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>{t.search.date}</label>
                    <input data-testid={`leg-date-${i}`} type="date" className={sel} value={l.date} onChange={(e) => setLeg(i, { date: e.target.value })} />
                  </div>
                  <button type="button" data-testid={`leg-remove-${i}`} disabled={legs.length <= 2} onClick={() => setLegs(legs.filter((_, j) => j !== i))}
                    className="h-[46px] w-full lg:w-11 rounded-xl border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-300 disabled:opacity-30 flex items-center justify-center"><X size={16} /></button>
                </div>
              ))}
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end justify-between">
                <div className="flex gap-3 items-end">
                  <button type="button" data-testid="leg-add" disabled={legs.length >= 5}
                    onClick={() => setLegs([...legs, { origin: legs[legs.length - 1].destination, destination: "BGW", date: "" }])}
                    className="px-4 h-[46px] rounded-xl border-2 border-dashed border-tigris/40 text-tigris text-sm font-bold hover:bg-tigris/5 disabled:opacity-30 flex items-center gap-1.5"><Plus size={16} /> {t.flights.addLeg}</button>
                  <div className="min-w-[180px]">
                    <label className={label}>{t.search.passengers}</label>
                    <PassengerPicker value={pax} onChange={setPax} className={sel} />
                  </div>
                </div>
                <button data-testid="flight-search-submit" onClick={submit} className="btn-primary px-8 py-3 rounded-xl font-bold flex items-center justify-center gap-2 h-[46px]">
                  <Search size={18} /> {t.hero.searchBtn}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {tab === "hotels" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
            <div className="lg:col-span-2">
              <label className={label}>{t.search.city}</label>
              <select data-testid="hotel-city" className={sel} value={ht.city} onChange={(e) => setHt({ ...ht, city: e.target.value })}>
                {CITIES.map((c) => <option key={c.id} value={c.id}>{c[lang]}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>{t.search.checkin}</label>
              <input data-testid="hotel-checkin" type="date" className={sel} value={ht.check_in} onChange={(e) => setHt({ ...ht, check_in: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t.search.checkout}</label>
              <input data-testid="hotel-checkout" type="date" className={sel} min={ht.check_in || undefined} value={ht.check_out} onChange={(e) => setHt({ ...ht, check_out: e.target.value })} />
            </div>
            <div>
              <label className={label}>{t.search.guests}</label>
              <button type="button" data-testid="hotel-guests-toggle" onClick={() => setShowGuests(!showGuests)} className={`${sel} text-start flex items-center gap-2`}>
                <Users size={15} className="text-tigris" /> {ht.adults} {t.guests.adultsShort} · {ht.children_ages.length} {t.guests.childrenShort} · {ht.rooms} {t.guests.roomsShort}
              </button>
            </div>
            <button data-testid="hotel-search-submit" onClick={submit} className="btn-primary py-3 rounded-xl font-bold flex items-center justify-center gap-2 h-[46px]">
              <Search size={18} /> {t.hero.searchBtn}
            </button>
          </div>
          {showGuests && <div className="bg-slate-50/80 rounded-2xl p-3 border border-slate-100"><HotelGuestPicker value={ht} onChange={setHt} showDates={false} /></div>}
        </div>
      )}

      {(tab === "religious" || tab === "tours") && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-3">
          <p className="text-slate-600 text-sm sm:text-base font-medium">
            {tab === "religious" ? t.sections.pkgSub : t.sections.destSub}
          </p>
          <button data-testid="package-search-submit" onClick={submit} className="btn-gold px-6 py-3 rounded-xl font-bold flex items-center gap-2 shrink-0">
            <ArrowRightLeft size={18} /> {t.common.viewAll}
          </button>
        </div>
      )}
    </div>
  );
};
